import { memo, useCallback } from 'react';
import { useResponsive } from 'app/hooks';
import { useVideoState, useSeek } from './hooks';
import { formatTime } from '../../../../media.utils';
import { SVG, HStack, Text } from 'app/pages/request-single-page/components-v4';
import { Icons } from 'app/pages/request-single-page/components-v4/icons';
import {
  IconWrapper,
  Overlay,
  SeekBar,
  SeekFill,
} from './video-controls.styled';

type VideoControlsProps = {
  getVideoEl: () => HTMLVideoElement | null;
  visible: boolean;
};

export const VideoControls = memo(
  ({ getVideoEl, visible }: VideoControlsProps) => {
    const { viewport } = useResponsive();

    const video = getVideoEl();

    const { playing, muted, ready, currentTime, duration } =
      useVideoState(video);

    const { seekRef, seekBarProps } = useSeek(video);

    const progress = duration > 0 ? currentTime / duration : 0;

    const togglePlay = useCallback(() => {
      if (!video) {
        return;
      }

      if (video.paused) {
        void video.play();
      } else {
        video.pause();
      }
    }, [video]);

    const toggleMute = useCallback(() => {
      if (!video) {
        return;
      }

      video.muted = !video.muted;
    }, [video]);

    return (
      <Overlay $visible={visible}>
        <HStack
          padding="2px 0"
          spacing="xs"
          align="center"
          style={{ color: 'white' }}
        >
          <IconWrapper onClick={togglePlay} disabled={!ready}>
            <SVG
              src={playing ? Icons.CirclePause : Icons.CirclePlay}
              props={{
                size: '24px',
                cursor: 'inherit',
              }}
            />
          </IconWrapper>

          <SeekBar ref={seekRef} {...seekBarProps}>
            <SeekFill style={{ width: `${progress * 100}%` }} />
          </SeekBar>

          <Text
            variant={viewport.mobile ? 'body-16' : 'body-14'}
            color="white"
            truncate
          >
            {formatTime(currentTime)} / {formatTime(duration)}
          </Text>

          <IconWrapper onClick={toggleMute} disabled={!ready}>
            <SVG
              src={muted ? Icons.VolumeOff : Icons.VolumeMedium}
              props={{
                size: '24px',
                cursor: 'inherit',
              }}
            />
          </IconWrapper>
        </HStack>
      </Overlay>
    );
  }
);
